import {toast} from '@wandb/components';
import React, {useCallback} from 'react';

import {trackCrossProjectPinnedRuns} from '../../../services/analytics';
import {useViewAction} from '../../../state/views/hooks';
import * as RunSetActions from '../../../state/views/runSet/actions';
import {isRunPinned} from '../../../state/views/runSet/utils';
import {MAX_PINNED_RUNS} from '../../../util/constants';
import type {WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {useRunSelectorFeatureEnabled} from '../RunSelectorFeaturesContext';
import {
  useCurrentRunSetRef,
  usePinnedAndBaseline,
  useRunSelectorProjectInfo,
  useRunSetGroupSelectionGrouping,
} from '../RunSelectorRunSetContext';
import {isCrossProjectRun, isRunBaseline} from '../util';

type GetBaselineLabelOpts = {
  isAnyRunBaseline: boolean;
  isBaseline: boolean;
};

function getBaselineLabel({
  isAnyRunBaseline,
  isBaseline,
}: GetBaselineLabelOpts): string {
  if (isBaseline) {
    return 'Remove baseline';
  } else if (isAnyRunBaseline) {
    return 'Replace baseline';
  } else {
    return 'Set baseline';
  }
}

type GetPinInfoLabelOpts = {
  isPinned: boolean;
  pinnedRunCount: number;
  maxPinnedRuns: number;
};

function getPinInfoLabel({
  isPinned,
  pinnedRunCount,
  maxPinnedRuns,
}: GetPinInfoLabelOpts): string | null {
  if (isPinned) {
    return null;
  }
  if (pinnedRunCount >= maxPinnedRuns) {
    return `${maxPinnedRuns} max`;
  }
  return `${pinnedRunCount} of ${maxPinnedRuns}`;
}

function pinLimitWarning() {
  toast.error(
    <>
      <strong>Pin limit reached.</strong> Unpin a few runs for greater
      usability.
    </>
  );
}

type UsePinningActionsOpts = {
  row: WBTableRowWithRunSetInfo;
};

export function usePinningActions({row}: UsePinningActionsOpts) {
  const isEnabled = useRunSelectorFeatureEnabled();
  const runSetRef = useCurrentRunSetRef();
  const {pinnedRunIds, baselineRunId} = usePinnedAndBaseline();
  const {entityName, projectName} = useRunSelectorProjectInfo();
  const grouping = useRunSetGroupSelectionGrouping();

  const isPinned = isRunPinned(pinnedRunIds, row);
  const isBaseline = isRunBaseline(baselineRunId, row);
  const isAnyRunBaseline = Boolean(baselineRunId);
  const setPinnedRun = useViewAction(runSetRef, RunSetActions.setPinnedRun);
  const setBaselineRun = useViewAction(runSetRef, RunSetActions.setBaselineRun);

  const canPinRun =
    isEnabled('pinning') && // run selector has pinning enabled
    pinnedRunIds.length < MAX_PINNED_RUNS && // haven't hit the pinning run limit
    grouping.length === 0; // runset has no grouping applied

  const canUnpinRun = isEnabled('pinning') && grouping.length === 0;

  const canBaselineRun =
    isEnabled('baselining') && // run selector has baselining enabled
    isEnabled('pinning') &&
    grouping.length === 0; // runset has no grouping applied

  const remainingPins = MAX_PINNED_RUNS - pinnedRunIds.length;

  const onPinClick = useCallback(() => {
    window.analytics?.track(isPinned ? 'Unpinned row' : 'Pinned row', {
      pinnedRunCount: pinnedRunIds.length,
    });
    if (
      isPinned &&
      isCrossProjectRun(row.project, {entityName, name: projectName})
    ) {
      trackCrossProjectPinnedRuns({
        action: 'Unpin cross-project run',
      });
    }
    setPinnedRun(row, !isPinned);

    if (!isPinned && remainingPins === 1) {
      pinLimitWarning();
    }
  }, [
    row,
    setPinnedRun,
    remainingPins,
    entityName,
    projectName,
    pinnedRunIds,
    isPinned,
  ]);
  const onBaselineClick = useCallback(() => {
    window.analytics?.track(isBaseline ? 'Removed baseline' : 'Added baseline');
    if (isCrossProjectRun(row.project, {entityName, name: projectName})) {
      trackCrossProjectPinnedRuns({
        action: isBaseline
          ? 'Remove cross-project run as baseline'
          : 'Set cross-project run as baseline',
      });
    }
    setBaselineRun(isBaseline ? null : row);

    // Setting a baseline implicitly pins the run (see setRunPinnedAndBaseline),
    // but only if the row isn't already pinned. Skip the warning when the
    // pin count won't actually change.
    if (!isBaseline && !isPinned && remainingPins === 1) {
      pinLimitWarning();
    }
  }, [
    row,
    setBaselineRun,
    remainingPins,
    entityName,
    projectName,
    isBaseline,
    isPinned,
  ]);

  const baselineLabel = getBaselineLabel({
    isAnyRunBaseline,
    isBaseline,
  });

  const pinInfoLabel = getPinInfoLabel({
    isPinned: isPinned,
    pinnedRunCount: pinnedRunIds.length,
    maxPinnedRuns: MAX_PINNED_RUNS,
  });

  return {
    baselineLabel,
    canBaselineRun,
    canPinRun,
    canUnpinRun,
    isBaseline,
    isPinned,
    onBaselineClick,
    onPinClick,
    pinLabel: isPinned ? 'Unpin run' : 'Pin run',
    pinInfoLabel,
  };
}
